'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { Can } from '@casl/react'
import { AppAbility, defineAbilitiesFor } from '@/@data/users/permission'
import { RootState, useAppSelector } from '@/redux-store'

interface CaslContextType {
  ability: AppAbility
  updateAbility: (user: any) => void
}

const CaslContext = createContext<CaslContextType | undefined>(undefined)

export const useCasl = () => {
  const context = useContext(CaslContext)

  if (!context) {
    throw new Error('useCasl must be used within a CaslProvider')
  }

  return context
}

export const CaslProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAppSelector((state: RootState) => state.userReducer)
  const [ability, setAbility] = useState<AppAbility>(defineAbilitiesFor(null))

  const updateAbility = (user: any) => {
    const newAbility = defineAbilitiesFor(user)

    setAbility(newAbility)
  }

  useEffect(() => {
    updateAbility(user)
  }, [user])

  const value = {
    ability,
    updateAbility
  }

  return <CaslContext.Provider value={value}>{children}</CaslContext.Provider>
}

// CASL Can Component wrapper
export const CaslCan: React.FC<{
  action: string
  subject: string
  children: React.ReactNode
  fallback?: React.ReactNode
}> = ({ action, subject, children }) => {
  return (
    <Can I={action} a={subject} ability={useCasl().ability}>
      {children}
    </Can>
  )
}

// Renders children in spite of what ability.can returns
export const CaslCanNotHide: React.FC<{
  action: string
  subject: string
  children: (allowed: boolean) => React.ReactNode
  fallback?: React.ReactNode
}> = ({ action, subject, children }) => {
  return (
    <Can I={action} a={subject} ability={useCasl().ability} passThrough>
      {children}
    </Can>
  )
}

// <CaslCan action='post' subject={SubjectTypeEnum}>
// </CaslCan>
